/**
 * megaGmaxSprites — runtime colour-art resolver for the Mega and G-Max main
 * dex grids. Primary source is PokeAPI high-res form art (official-artwork /
 * home, keyed by the form's own pokemon id); the older pokesprite gen8/gen7x
 * pixel slugs are retained only as a last-resort 404 fallback.
 *
 * WHY THIS EXISTS
 * ---------------
 * The main Mega / G-Max dex grids render one cell per BASE national-dex id
 * (see MEGA_DEX_IDS / GMAX_DEX_IDS in `pogoAvailability.ts`). The caught-cell
 * colour renderer (`CaughtSprite`) historically resolved art purely by base
 * id via PokeAPI official-artwork — which has NO Mega/G-Max forms, so a caught
 * Mega Charizard cell showed plain Charizard. This module supplies the proper
 * form art by mapping (variant, baseId) -> the msikma/pokesprite slug used by
 * the bundled silhouette atlas, then building the gen8 -> gen7x candidate URLs.
 *
 * SOURCE OF TRUTH
 * ---------------
 * The slug table below is the runtime mirror of `FORM_SOURCES` in
 * `scripts/silhouettes/sources.ts` (the build-time silhouette pipeline). The
 * two MUST stay in sync; `megaFormSourcesSync.test.ts` (a vitest suite wired
 * into `npm test`) compares the two tables in both directions, and
 * `megaGmaxSprites.test.ts` asserts every roster id has a slug. When new
 * Megas / G-Max release, append to BOTH this table and FORM_SOURCES.
 *
 * pokesprite naming (verified against the repo):
 *   - Megas live in gen8 AND gen7x as `{base}-mega`, `{base}-mega-x`,
 *     `{base}-mega-y`.
 *   - G-Max live only in gen8 as `{base}-gmax`.
 *   - Charizard (6) and Mewtwo (150) have X & Y Megas but a single base-species
 *     grid cell; we use the X form as the representative cell sprite.
 *   - Urshifu (892) Single-Strike G-Max is `urshifu-gmax`.
 *   - `appletun-gmax` is NOT shipped by pokesprite; ids without a slug simply
 *     return no candidates and CaughtSprite falls back to base art.
 */

import type { PokedexVariant } from "@/lib/pogoPokedexVariantClassifier";
import { MEGA_ROSTER_OVERRIDES, isMegaOverrideReleased } from "@/lib/megaRosterOverrides";
import { SPRITE_CDN_POKEAPI_OTHER, SPRITE_CDN_POKESPRITE } from "@/lib/spriteCdn";

const POKESPRITE_GEN8 = `${SPRITE_CDN_POKESPRITE}/pokemon-gen8/regular`;
const POKESPRITE_GEN7X = `${SPRITE_CDN_POKESPRITE}/pokemon-gen7x/regular`;

/**
 * High-res colour-art base, mirroring `pokemonArtwork.ts`. Mega/G-Max renders
 * are keyed by the FORM's own pokemon id (e.g. 10034 = Mega Charizard X), NOT
 * the base national-dex id — so we cannot reuse the base-id URL shape from
 * pokemonArtwork.ts here. official-artwork is 475x475, home is 512x512: the
 * same premium art the caught-cell renderer already uses for non-form species.
 * These replace the 68x56 pokesprite pixel art as the primary source;
 * pokesprite remains only as a last-resort 404 fallback.
 *
 * Everything here resolves to our own mirror (spriteCdn.ts). PokeAPI and
 * pokesprite are build-time sources via scripts/sync-sprites-to-r2.ts, never
 * runtime dependencies.
 */
const POKEAPI_OTHER = SPRITE_CDN_POKEAPI_OTHER;

/**
 * Base national-dex id -> PokeAPI FORM pokemon id, per variant. Verified
 * against the live PokeAPI: every id below has both official-artwork AND home
 * renders present. X/Y Megas (Charizard 6, Mewtwo 150) use the X form id to
 * match the single representative grid cell (consistent with MEGA_SLUG_BY_ID).
 * When new Megas / G-Max release, append the form id here alongside the slug.
 *
 * This is the PoGoAPI-aligned CORE table. Announced-but-not-yet-in-PoGoAPI
 * Megas (e.g. the Super Max additions) live in the auditable overlay
 * `megaRosterOverrides.ts` and are merged in below — keep this core in step
 * with the PoGoAPI feed (see scripts/generate-mega-roster.ts).
 */
const MEGA_FORM_ID_BY_BASE_CORE: Readonly<Record<number, number>> = {
  3: 10033, // venusaur-mega
  6: 10034, // charizard-mega-x
  9: 10036, // blastoise-mega
  15: 10090, // beedrill-mega
  18: 10073, // pidgeot-mega
  65: 10037, // alakazam-mega
  80: 10071, // slowbro-mega
  94: 10038, // gengar-mega
  115: 10039, // kangaskhan-mega
  127: 10040, // pinsir-mega
  130: 10041, // gyarados-mega
  142: 10042, // aerodactyl-mega
  150: 10043, // mewtwo-mega-x
  181: 10045, // ampharos-mega
  208: 10072, // steelix-mega
  212: 10046, // scizor-mega
  214: 10047, // heracross-mega
  229: 10048, // houndoom-mega
  248: 10049, // tyranitar-mega
  254: 10065, // sceptile-mega
  257: 10050, // blaziken-mega
  260: 10064, // swampert-mega
  282: 10051, // gardevoir-mega
  302: 10066, // sableye-mega
  303: 10052, // mawile-mega
  306: 10053, // aggron-mega
  308: 10054, // medicham-mega
  310: 10055, // manectric-mega
  319: 10070, // sharpedo-mega
  323: 10087, // camerupt-mega
  334: 10067, // altaria-mega
  354: 10056, // banette-mega
  359: 10057, // absol-mega
  362: 10074, // glalie-mega
  373: 10089, // salamence-mega
  376: 10076, // metagross-mega
  380: 10062, // latias-mega
  381: 10063, // latios-mega
  // Primal Reversion (2026-09-09): a Mega in every mechanic the app models,
  // named "Primal" (see megaLevels.megaNamePrefix). These are PokeAPI POKEMON ids
  // (/pokemon/kyogre-primal = 10077), NOT pokemon-form ids: 10179 in the pokemon
  // namespace is Galarian Yamask, which is what the cell drew on 2026-09-11.
  382: 10077, // kyogre-primal
  383: 10078, // groudon-primal
  384: 10079, // rayquaza-mega
  428: 10088, // lopunny-mega
  445: 10058, // garchomp-mega
  448: 10059, // lucario-mega
  460: 10060, // abomasnow-mega
  475: 10068, // gallade-mega
  531: 10069, // audino-mega
  719: 10075, // diancie-mega
};

const GMAX_FORM_ID_BY_BASE: Readonly<Record<number, number>> = {
  3: 10195, // venusaur-gmax
  6: 10196, // charizard-gmax
  9: 10197, // blastoise-gmax
  12: 10198, // butterfree-gmax
  25: 10199, // pikachu-gmax
  52: 10200, // meowth-gmax
  68: 10201, // machamp-gmax
  94: 10202, // gengar-gmax
  99: 10203, // kingler-gmax
  131: 10204, // lapras-gmax
  133: 10205, // eevee-gmax
  143: 10206, // snorlax-gmax
  569: 10207, // garbodor-gmax
  809: 10208, // melmetal-gmax
  812: 10209, // rillaboom-gmax
  815: 10210, // cinderace-gmax
  818: 10211, // inteleon-gmax
  823: 10212, // corviknight-gmax
  826: 10213, // orbeetle-gmax
  834: 10214, // drednaw-gmax
  839: 10215, // coalossal-gmax
  841: 10216, // flapple-gmax
  842: 10217, // appletun-gmax (PokeAPI art only — no pokesprite slug shipped)
  844: 10218, // sandaconda-gmax
  849: 10219, // toxtricity-amped-gmax (PokeAPI has no plain 'toxtricity-gmax')
  851: 10220, // centiskorch-gmax
  861: 10222, // grimmsnarl-gmax
  869: 10223, // alcremie-gmax
  879: 10224, // copperajah-gmax
  884: 10225, // duraludon-gmax
  892: 10226, // urshifu-single-strike-gmax (Single-Strike = representative cell)
};

/**
 * Representative pokesprite slug for each base national-dex id, per variant.
 * Megas with X/Y (Charizard 6, Mewtwo 150) use the X form for the single grid
 * cell. Mirror of FORM_SOURCES (scripts/silhouettes/sources.ts).
 *
 * PoGoAPI-aligned CORE table; overlay additions are merged in below from
 * `megaRosterOverrides.ts`.
 */
const MEGA_SLUG_BY_ID_CORE: Readonly<Record<number, string>> = {
  3: "venusaur-mega",
  6: "charizard-mega-x", // X & Y share one base cell; X is representative
  9: "blastoise-mega",
  15: "beedrill-mega",
  18: "pidgeot-mega",
  65: "alakazam-mega",
  80: "slowbro-mega",
  94: "gengar-mega",
  115: "kangaskhan-mega",
  127: "pinsir-mega",
  130: "gyarados-mega",
  142: "aerodactyl-mega",
  150: "mewtwo-mega-x", // X & Y share one base cell; X is representative
  181: "ampharos-mega",
  208: "steelix-mega",
  212: "scizor-mega",
  214: "heracross-mega",
  229: "houndoom-mega",
  248: "tyranitar-mega",
  254: "sceptile-mega",
  257: "blaziken-mega",
  260: "swampert-mega",
  282: "gardevoir-mega",
  302: "sableye-mega",
  303: "mawile-mega",
  306: "aggron-mega",
  308: "medicham-mega",
  310: "manectric-mega",
  319: "sharpedo-mega",
  323: "camerupt-mega",
  334: "altaria-mega",
  354: "banette-mega",
  359: "absol-mega",
  362: "glalie-mega",
  373: "salamence-mega",
  376: "metagross-mega",
  380: "latias-mega",
  381: "latios-mega",
  382: "kyogre-primal", // Primal Reversion, pokesprite's own slug
  383: "groudon-primal",
  384: "rayquaza-mega",
  428: "lopunny-mega",
  445: "garchomp-mega",
  448: "lucario-mega",
  460: "abomasnow-mega",
  475: "gallade-mega",
  531: "audino-mega",
  719: "diancie-mega",
};

/**
 * Runtime Mega tables = PoGoAPI-aligned core MERGED with the auditable overlay
 * (`megaRosterOverrides.ts`). The overlay carries announced-but-missing Megas
 * (Super Max / Super Mega Raid debuts) that PoGoAPI's feed still lags on, each
 * with source URLs and review/expiry metadata. Merging here means MEGA_DEX_IDS
 * (derived from the slug table keys) and the high-res art resolver both pick up
 * overlay entries automatically, with no separate dex type and no schema churn.
 *
 * An overlay entry never silently clobbers a core entry: if a baseId somehow
 * appears in both, the core (PoGoAPI-aligned) value wins, so the overlay can
 * only ADD missing species, never rewrite confirmed ones.
 */
function mergeOverlay<T>(
  core: Readonly<Record<number, T>>,
  pick: (override: (typeof MEGA_ROSTER_OVERRIDES)[number]) => T,
): Readonly<Record<number, T>> {
  const merged: Record<number, T> = { ...core };
  for (const o of MEGA_ROSTER_OVERRIDES) {
    if (o.baseId in merged) continue; // core (PoGoAPI-aligned) value wins
    // A future-dated entry (releaseAtLocal) stays out of BOTH merged tables
    // until its local release moment, so roster and art flip together — the
    // GO Fest Mega Finale starters ship in the build but only appear once
    // the event starts on the device's own clock.
    if (!isMegaOverrideReleased(o)) continue;
    merged[o.baseId] = pick(o);
  }
  return merged;
}

const MEGA_SLUG_BY_ID: Readonly<Record<number, string>> = mergeOverlay(
  MEGA_SLUG_BY_ID_CORE,
  (o) => o.slug,
);

const MEGA_FORM_ID_BY_BASE: Readonly<Record<number, number>> = mergeOverlay(
  MEGA_FORM_ID_BY_BASE_CORE,
  (o) => o.formId,
);

// ── X/Y secondary Mega forms (pokedex-mega-xy) ─────────────────────────────
//
// Charizard (#6), Mewtwo (#150) and Raichu (#26) each have TWO Mega forms that
// Pokémon GO lists as separate Pokédex cells. The base id keeps the X form (so
// existing `mega:6` catches + scans are unchanged), and each Y form renders as
// its own cell keyed by a form id (`mega:6:charizard-mega-y`). These are all
// PokeAPI forms, so their high-res art (10035 / 10044 / 10305) is reliable.
//
// Raichu is the first PRE-RELEASE member: Mega Raichu debuts 2026-07-18 (Super
// Mega Raid Day) and GO's own dex screen already shows the MEGA X / MEGA Y
// cells. Its roster membership comes from megaRosterOverrides (the Game Master
// has no Mega Raichu yet — Niantic ships data at release).

export interface MegaForm {
  /** Base national-dex id the form belongs to (display number + name). */
  readonly baseId: number;
  /** Store form id, e.g. "charizard-mega-y" (keys `mega:6:charizard-mega-y`). */
  readonly formId: string;
  /** Short badge shown on the cell to tell the pair apart ("X" / "Y"). */
  readonly label: string;
  /** pokesprite slug (last-resort art). */
  readonly slug: string;
  /** PokeAPI FORM pokemon id for high-res art, or null when none ships yet. */
  readonly pokeApiFormId: number | null;
}

/** Secondary Mega forms rendered as their OWN grid cell beside the base cell. */
export const MEGA_EXTRA_FORMS: readonly MegaForm[] = [
  { baseId: 6, formId: "charizard-mega-y", label: "Y", slug: "charizard-mega-y", pokeApiFormId: 10035 },
  { baseId: 150, formId: "mewtwo-mega-y", label: "Y", slug: "mewtwo-mega-y", pokeApiFormId: 10044 },
  { baseId: 26, formId: "raichu-mega-y", label: "Y", slug: "raichu-mega-y", pokeApiFormId: 10305 },
];

/** The label for a base Mega cell that is one of an X/Y pair (empty otherwise). */
const MEGA_BASE_FORM_LABEL: Readonly<Record<number, string>> = {
  6: "X",
  150: "X",
  26: "X",
};

/**
 * Secondary G-Max forms rendered as their OWN grid cell (2026-07-24).
 *
 * Urshifu (#892) is the only G-Max with two styles: Single-Strike and
 * Rapid-Strike each get a cell in Pokémon GO's G-Max dex (device screenshot
 * 2026-07-24). Single-Strike is the base cell (`gmax:892`, GMAX_SLUG_BY_ID);
 * Rapid-Strike is the secondary cell here. pokesprite ships
 * `urshifu-rapid-strike-gmax` (scripts/silhouettes/sources.ts), so the pixel
 * tail always resolves even if the PokeAPI form-art id (10227) ever lags.
 */
export const GMAX_EXTRA_FORMS: readonly MegaForm[] = [
  { baseId: 892, formId: "urshifu-rapid-strike-gmax", label: "Rapid", slug: "urshifu-rapid-strike-gmax", pokeApiFormId: 10227 },
];

/** The label for a base G-Max cell that is one of a pair (empty otherwise). */
const GMAX_BASE_FORM_LABEL: Readonly<Record<number, string>> = {
  892: "Single",
};

/** Secondary Mega forms for a base id (empty for the common single-form case). */
export function megaExtraFormsFor(baseId: number): MegaForm[] {
  return MEGA_EXTRA_FORMS.filter((f) => f.baseId === baseId);
}

/**
 * Secondary form cells for a (variant, baseId), in on-screen order after the
 * base cell. Mega X/Y and G-Max Urshifu Single/Rapid are the only cases; empty
 * for every single-cell species and every other variant. This is the single
 * source the grid renderer and the store cascade share, so a cell rendered is a
 * cell that can be caught.
 */
export function dexExtraFormsFor(variant: PokedexVariant, baseId: number): MegaForm[] {
  if (variant === "mega") return MEGA_EXTRA_FORMS.filter((f) => f.baseId === baseId);
  if (variant === "gmax") return GMAX_EXTRA_FORMS.filter((f) => f.baseId === baseId);
  return [];
}

/** X/Y badge for a Mega cell (base or extra form); null when the species is single-form. */
export function megaFormLabel(baseId: number, formId?: string | null): string | null {
  if (formId) return MEGA_EXTRA_FORMS.find((f) => f.formId === formId)?.label ?? null;
  return MEGA_BASE_FORM_LABEL[baseId] ?? null;
}

/**
 * Cell badge for a Mega/G-Max form cell (base or extra), or null when the
 * species is single-form. "X"/"Y" for Mega, "Single"/"Rapid" for G-Max Urshifu.
 */
export function dexFormLabel(
  variant: PokedexVariant,
  baseId: number,
  formId?: string | null,
): string | null {
  const extras = variant === "mega" ? MEGA_EXTRA_FORMS : variant === "gmax" ? GMAX_EXTRA_FORMS : [];
  if (formId) return extras.find((f) => f.formId === formId)?.label ?? null;
  const baseLabels = variant === "mega" ? MEGA_BASE_FORM_LABEL : variant === "gmax" ? GMAX_BASE_FORM_LABEL : {};
  return baseLabels[baseId] ?? null;
}

/**
 * How many on-screen Pokédex CELLS a base species occupies in `variant`'s grid.
 *
 * 1 for the overwhelming common case; more for the handful of species GO lists
 * as multiple form cells sharing one dex number — Mega Charizard/Mewtwo/Raichu
 * (X and Y) and G-Max Urshifu (two styles). The base roster records each such
 * species ONCE (it is keyed by base species everywhere it matters), so this is
 * the single place that knows a base id can render as several adjacent cells.
 *
 * Used by the post-scan coverage math to split rows the way the PoGo grid
 * actually lays them out. Verified against device screenshots on 2026-07-23:
 * Kanto Mega is 19 cells (16 species + Charizard/Raichu/Mewtwo's Y forms) and
 * the in-game counter reads 19, not 16.
 */
export function dexCellCountFor(variant: PokedexVariant, baseId: number): number {
  return 1 + dexExtraFormIdsFor(variant, baseId).length;
}

/**
 * Store form ids for a base species' SECONDARY cells in `variant`'s grid, in the
 * order GO renders them AFTER the base cell (left → right).
 *
 * Empty for the common single-cell species. For the four two-cell species this
 * is the one entry each: Mega Charizard/Mewtwo/Raichu → their `-mega-y` form,
 * G-Max Urshifu → its Rapid-Strike form. The base (first) cell always carries no
 * form id — it is the plain `mega:N` / `gmax:N` key the store already uses — so
 * the k-th on-screen cell of a species maps to element k-1 here (k=0 is base).
 *
 * This is the single source the live scanner uses to tag the k-th same-numbered
 * cell it reads with the right form id, so X and Y (or Single/Rapid) write to
 * their own store rows instead of colliding on the base row. Returned as store
 * form ids (not the rich MegaForm) because that is all the write path needs.
 */
export function dexExtraFormIdsFor(
  variant: PokedexVariant,
  baseId: number,
): readonly string[] {
  return dexExtraFormsFor(variant, baseId).map((f) => f.formId);
}

/**
 * The Y-form MegaForm when a display name shows the Y Mega of an X/Y pair
 * ("Mega Charizard Y", "Mega Mewtwo Y"), else null.
 *
 * Raid rosters carry NO form id for Megas — the upstream display name is the
 * only X/Y signal — so Mega raid art/silhouette resolution goes through this.
 * X and single-form Megas return null: the representative base cell (which IS
 * the X form for the X/Y species) is already the right art for them.
 */
export function megaFormForDisplayName(
  baseId: number,
  name: string | null | undefined,
): MegaForm | null {
  if (!name || !/[\s-]y$/i.test(name.trim())) return null;
  return MEGA_EXTRA_FORMS.find((f) => f.baseId === baseId && f.label === "Y") ?? null;
}

/**
 * Best-quality-first art candidate URLs for a secondary Mega form cell.
 * `shiny` (2026-09-03, the Mega view's Shiny copy) swaps in PokeAPI's shiny
 * official-artwork and HOME renders; the pixel fallbacks stay regular, so a
 * shiny that has no hi-res art still draws the form rather than nothing.
 */
export function megaFormSpriteCandidates(form: MegaForm, opts?: { shiny?: boolean }): string[] {
  const urls: string[] = [];
  const shiny = opts?.shiny ? "shiny/" : "";
  if (form.pokeApiFormId != null) {
    urls.push(`${POKEAPI_OTHER}/official-artwork/${shiny}${form.pokeApiFormId}.png`);
    urls.push(`${POKEAPI_OTHER}/home/${shiny}${form.pokeApiFormId}.png`);
  }
  urls.push(`${POKESPRITE_GEN8}/${form.slug}.png`);
  urls.push(`${POKESPRITE_GEN7X}/${form.slug}.png`);
  return urls;
}

const GMAX_SLUG_BY_ID: Readonly<Record<number, string>> = {
  3: "venusaur-gmax",
  6: "charizard-gmax",
  9: "blastoise-gmax",
  12: "butterfree-gmax",
  25: "pikachu-gmax",
  52: "meowth-gmax",
  68: "machamp-gmax",
  94: "gengar-gmax",
  99: "kingler-gmax",
  131: "lapras-gmax",
  133: "eevee-gmax",
  143: "snorlax-gmax",
  569: "garbodor-gmax",
  809: "melmetal-gmax",
  812: "rillaboom-gmax",
  815: "cinderace-gmax",
  818: "inteleon-gmax",
  823: "corviknight-gmax",
  826: "orbeetle-gmax",
  834: "drednaw-gmax",
  839: "coalossal-gmax",
  841: "flapple-gmax",
  // appletun-gmax pixel art is NOT shipped by pokesprite (see sources.ts); the
  // slug still keys roster membership and the gen8 URL simply 404s, so the
  // resolver falls through to the PokeAPI form art (10217) that leads the chain.
  842: "appletun-gmax",
  844: "sandaconda-gmax",
  849: "toxtricity-gmax",
  851: "centiskorch-gmax",
  861: "grimmsnarl-gmax",
  869: "alcremie-gmax",
  879: "copperajah-gmax",
  // 2026-10-25: Gigantamax Duraludon announced for GO Wild Area 2026
  // (2026-11-06); listed from the announcement (owner rule: no date locks).
  884: "duraludon-gmax",
  892: "urshifu-gmax", // Single-Strike is the representative cell sprite
};

/** True when this variant renders Mega/G-Max form art. */
export function isMegaGmaxVariant(variant: PokedexVariant): boolean {
  return variant === "mega" || variant === "gmax";
}

/**
 * Resolve the pokesprite slug for a (variant, baseId) main-grid cell, or null
 * when the variant is not mega/gmax or the id has no shipped form sprite.
 */
/* ────────────────────────────────────────────────────────────────────────────
   Server-driven sprite provenance for admin-added Mega/G-Max members.
   ----------------------------------------------------------------------------
   When an admin adds a Mega/G-Max via the dex-config editor with a PokeAPI form
   id + slug, the dex-config syncer feeds it here. megaGmaxSlug / megaGmaxFormId
   consult it FIRST, so an admin-added member resolves art through the exact same
   path as a bundled megaRosterOverride (Raichu / Starmie):
   sprites.nexusdex.ai/.../official-artwork/{formId}.png. Keyed "variant:baseId".
   ──────────────────────────────────────────────────────────────────────────── */
const runtimeMegaSpriteOverrides = new Map<string, { slug: string; formId: number | null }>();

export interface MegaSpriteOverride {
  variant: "mega" | "gmax";
  baseId: number;
  slug: string;
  formId: number | null;
}

/** Replace the runtime Mega/G-Max sprite overrides (from GET /api/dex-config). */
export function setRuntimeMegaSpriteOverrides(entries: readonly MegaSpriteOverride[]): void {
  runtimeMegaSpriteOverrides.clear();
  for (const e of entries ?? []) {
    if ((e.variant === "mega" || e.variant === "gmax") && Number.isFinite(e.baseId) && e.slug) {
      runtimeMegaSpriteOverrides.set(`${e.variant}:${e.baseId}`, {
        slug: e.slug,
        formId: typeof e.formId === "number" ? e.formId : null,
      });
    }
  }
}

export function megaGmaxSlug(
  variant: PokedexVariant,
  baseId: number,
): string | null {
  const ov = runtimeMegaSpriteOverrides.get(`${variant}:${baseId}`);
  if (ov) return ov.slug;
  if (variant === "mega") return MEGA_SLUG_BY_ID[baseId] ?? null;
  if (variant === "gmax") return GMAX_SLUG_BY_ID[baseId] ?? null;
  return null;
}

/**
 * Translate a Mega/G-Max cell into the silhouette-atlas `forms` key.
 *
 * The bundled atlas (dex-silhouettes.atlas.json) keys its form tiles by the
 * STABLE atlas slug from FORM_SOURCES (scripts/silhouettes/sources.ts) —
 * "{baseId}-mega", "{baseId}-mega-x", "{baseId}-gmax", and positionally
 * "892-1-gmax"/"892-2-gmax" for Urshifu's two styles — NOT by the pokesprite
 * name slug ("venusaur-mega") this module resolves art URLs with. Any
 * `formSlug` handed to PokemonSilhouette/CaughtSprite must be the ATLAS slug,
 * so grid cells translate through here; passing the pokesprite slug always
 * misses the `forms` map and silently renders the base-species shape.
 *
 * `spriteSlug` overrides the base-cell lookup for SECONDARY form cells
 * (charizard-mega-y, urshifu-rapid-strike-gmax); omitted, the (variant,
 * baseId) representative slug is translated. Returns null off-roster or for
 * non-form variants. Every current roster cell has a shipped tile
 * (2026-08-24 append — the roster-wide atlas test in megaGmaxSprites.test.ts guards
 * this); should a future roster addition land before its tile, the
 * translated slug misses the `forms` map and the base-species tile is the
 * fallback until scripts/silhouettes/append-forms.ts runs.
 */
export function megaGmaxAtlasFormSlug(
  variant: PokedexVariant,
  baseId: number,
  spriteSlug?: string | null,
): string | null {
  const slug = spriteSlug ?? megaGmaxSlug(variant, baseId);
  if (!slug) return null;
  return translateSpriteSlugToAtlas(baseId, slug);
}

/**
 * Atlas form slug for a bare store/registry form id, or undefined when the id
 * names no Mega/G-Max form. For surfaces that carry a per-record `formId` with
 * no dex variant in scope (My Pokemon records, raid bosses): the Mega/G-Max
 * store ids ("charizard-mega-y", "urshifu-rapid-strike-gmax") share the
 * pokesprite suffix shape and translate; regional/costume registry ids
 * ("darumaka-galar", "bulbasaur-fall-2019") have no atlas tile and yield
 * undefined, keeping the base-species silhouette those forms should show.
 */
export function atlasFormSlugForFormId(
  baseId: number,
  formId: string | null | undefined,
): string | undefined {
  if (!formId) return undefined;
  return translateSpriteSlugToAtlas(baseId, formId) ?? undefined;
}

function translateSpriteSlugToAtlas(baseId: number, slug: string): string | null {
  // Urshifu's two G-Max styles are keyed positionally in the atlas.
  if (slug === "urshifu-gmax") return "892-1-gmax";
  if (slug === "urshifu-rapid-strike-gmax") return "892-2-gmax";
  if (slug.endsWith("-mega-x")) return `${baseId}-mega-x`;
  if (slug.endsWith("-mega-y")) return `${baseId}-mega-y`;
  if (slug.endsWith("-mega")) return `${baseId}-mega`;
  // Primal Reversion (Kyogre / Groudon, 2026-09-09) lives in the Mega dex and
  // takes the Mega atlas key; only its pokesprite slug says "primal".
  if (slug.endsWith("-primal")) return `${baseId}-mega`;
  if (slug.endsWith("-gmax")) return `${baseId}-gmax`;
  // Armored Mewtwo (2026-09-07): the one GO-only non-Mega form with its own
  // atlas tile (scripts/silhouettes/sources.ts, kind "form").
  if (slug === "mewtwo-armored" && baseId === 150) return "150-armored";
  return null;
}

/**
 * Resolve the PokeAPI FORM pokemon id for a (variant, baseId) main-grid cell,
 * or null when the variant is not mega/gmax or the id has no high-res form
 * render. This is the high-res counterpart to `megaGmaxSlug`.
 */
export function megaGmaxFormId(
  variant: PokedexVariant,
  baseId: number,
): number | null {
  // A portal add may carry a slug but no form id (the field is optional), and
  // for a while that BLANKED the bundled form id for the same cell: Mega
  // Starmie's runtime row won with formId null, both high-res candidates were
  // skipped, and the only ones left were pokesprite slugs that 404 for any
  // GO-first Mega. A partial override must not erase good bundled provenance,
  // so fall through instead of returning the null.
  const ov = runtimeMegaSpriteOverrides.get(`${variant}:${baseId}`);
  if (ov && ov.formId != null) return ov.formId;
  if (variant === "mega") return MEGA_FORM_ID_BY_BASE[baseId] ?? null;
  if (variant === "gmax") return GMAX_FORM_ID_BY_BASE[baseId] ?? null;
  return null;
}

/**
 * Ordered colour-art candidate URLs for a (variant, baseId) cell, best quality
 * first. The caller (`CaughtSprite`) tries each in turn via onError:
 *   1. PokeAPI official-artwork (475x475) keyed by form id  -- premium primary
 *   2. PokeAPI HOME render      (512x512) keyed by form id  -- premium fallback
 *   3. pokesprite gen8 pixel slug                           -- last-resort
 *   4. pokesprite gen7x pixel slug (Megas only)             -- last-resort
 * Empty array when there is no form art — the caller then falls back to base
 * official-artwork. The pokesprite tail guarantees no regression for any id
 * the high-res sets ever lag on; in practice (1) lands for every roster id.
 */
export function megaGmaxSpriteCandidates(
  variant: PokedexVariant,
  baseId: number,
  opts?: { shiny?: boolean },
): string[] {
  const slug = megaGmaxSlug(variant, baseId);
  if (!slug) return [];
  const urls: string[] = [];
  const formId = megaGmaxFormId(variant, baseId);
  const shiny = opts?.shiny ? "shiny/" : "";
  if (formId != null) {
    urls.push(`${POKEAPI_OTHER}/official-artwork/${shiny}${formId}.png`);
    urls.push(`${POKEAPI_OTHER}/home/${shiny}${formId}.png`);
  }
  // pokesprite pixel art kept as a last-resort 404 safety net only.
  urls.push(`${POKESPRITE_GEN8}/${slug}.png`);
  // gen7x carries Megas (not G-Max) as a fallback; harmless to offer for gmax
  // because an earlier candidate lands first and gen7x simply 404s -> skipped.
  if (variant === "mega") urls.push(`${POKESPRITE_GEN7X}/${slug}.png`);
  return urls;
}

/**
 * Colour-art candidates for a (variant, baseId, formId) row — the
 * store-form-aware superset of `megaGmaxSpriteCandidates`, matching how the
 * dex grid resolves art: a formId naming one of the secondary cells
 * ("charizard-mega-y", "urshifu-rapid-strike-gmax") resolves that form's own
 * art, any other formId (or none) resolves the representative base-cell art.
 * Empty for non-mega/gmax variants and off-roster ids, keeping the caller's
 * base-species fallback. For surfaces that render dex rows OUTSIDE the grid
 * (scan history) so they cannot half-diverge from what the grid draws.
 */
export function dexFormArtCandidates(
  variant: PokedexVariant,
  baseId: number,
  formId?: string | null,
): string[] {
  if (!isMegaGmaxVariant(variant)) return [];
  if (formId) {
    const extra = dexExtraFormsFor(variant, baseId).find((f) => f.formId === formId);
    if (extra) return megaFormSpriteCandidates(extra);
  }
  return megaGmaxSpriteCandidates(variant, baseId);
}

/**
 * BUILD-TIME ONLY: candidate URLs for overlay Megas the release gate still
 * hides (releaseAtLocal in the future). The R2 mirror and the offline sprite
 * bundle run BEFORE an event ships, and must carry a future-dated Mega's art
 * ahead of time — otherwise the cells flip live on event morning with nothing
 * to draw. Same URL shape/order as megaGmaxSpriteCandidates; runtime code has
 * no reason to call this.
 */
export function futureMegaOverrideSpriteCandidates(): string[] {
  const urls: string[] = [];
  for (const o of MEGA_ROSTER_OVERRIDES) {
    if (isMegaOverrideReleased(o)) continue;
    if (o.baseId in MEGA_SLUG_BY_ID_CORE) continue; // core wins; nothing new to mirror
    urls.push(`${POKEAPI_OTHER}/official-artwork/${o.formId}.png`);
    urls.push(`${POKEAPI_OTHER}/home/${o.formId}.png`);
    urls.push(`${POKESPRITE_GEN8}/${o.slug}.png`);
    urls.push(`${POKESPRITE_GEN7X}/${o.slug}.png`);
  }
  return urls;
}

/**
 * Every base id in the Mega roster: the bundled table (core + released
 * overlay) UNION any admin-added members from the dex-config portal, dex
 * order.
 *
 * megaLevels reads the roster through here rather than the bundled table
 * directly. Before that, a portal-added Mega (Starmie, 2026-09-03) was a
 * roster member with sprite provenance but no MegaFormRef, so it had no Mega
 * view on the species page, no Mega Levels on a record, no admin event row,
 * and event tiles labelled it with the bare species name.
 *
 * Note the asymmetry this cannot fix: the silhouette atlas is a BUNDLED
 * binary keyed by FORM_SOURCES, so a portal-added Mega still ships with no
 * uncaught tile until it is promoted into megaRosterOverrides.ts. Runtime
 * adds get colour art and every Mega surface; the silhouette needs a build.
 */
export function megaRosterBaseIds(): number[] {
  const ids = new Set<number>(Object.keys(MEGA_SLUG_BY_ID).map(Number));
  for (const key of runtimeMegaSpriteOverrides.keys()) {
    const [variant, baseId] = key.split(":");
    if (variant !== "mega") continue;
    const n = Number(baseId);
    if (Number.isInteger(n) && n > 0) ids.add(n);
  }
  return [...ids].sort((a, b) => a - b);
}

/** Exposed for the in-sync test against scripts/silhouettes/sources.ts. */
export const __MEGA_SLUG_BY_ID = MEGA_SLUG_BY_ID;
/** Exposed for the mega-roster audit's release-gate validation. */
export const __MEGA_SLUG_BY_ID_CORE = MEGA_SLUG_BY_ID_CORE;
export const __GMAX_SLUG_BY_ID = GMAX_SLUG_BY_ID;
/** Exposed for the form-id coverage test. */
export const __MEGA_FORM_ID_BY_BASE = MEGA_FORM_ID_BY_BASE;
export const __GMAX_FORM_ID_BY_BASE = GMAX_FORM_ID_BY_BASE;
